import { computed, makeObservable } from "mobx";
import { ViewModelBase } from "@shoothill/core";

import { AddStudentModelModelValidator, AddStudentModel } from "./AddStudentModel";
import { ManagersModule } from "./Managers";

export class AddStudentViewModel extends ViewModelBase<AddStudentModel> {
    public managers!: ManagersModule;

    constructor(addStudentModel: AddStudentModel | null) {
        super(addStudentModel ?? new AddStudentModel());
        this.setValidator(new AddStudentModelModelValidator());
        this.managers = new ManagersModule(this);

        makeObservable(this, {
            addStudentMentorRotaButtonName: computed,
            startTimeAsDate: computed,
            endTimeAsDate: computed,
        });
    }

    /** Loads students. Called from the parent ViewModel's initialize(), not from this constructor. */
    public async initialize(): Promise<void> {
        await this.managers.api.loadStudents();
    }

    // ─── Domain data — delegated to DomainManager ─────────────────────────────

    public get studentOptions() {
        return this.managers.domain.studentOptions;
    }
    public get studentAddressOptions() {
        return this.managers.domain.studentAddressOptions;
    }
    public get getAbsenceShift() {
        return this.managers.domain.absenceShiftOptions;
    }
    public get getAbsenceTime() {
        return this.managers.domain.absenceTimeOptions;
    }

    public get addStudentMentorRotaButtonName(): string {
        return this.managers.domain.addButtonDisplayName;
    }

    // ─── Utility ──────────────────────────────────────────────────────────────

    public convertTimeToDate(value: any): Date {
        return this.managers.domain.convertTimeToDate(value);
    }

    /** Start time field value converted to a Date for the time picker. */
    public get startTimeAsDate(): Date {
        return this.managers.domain.convertTimeToDate(this.getValue("startTime"));
    }

    /** End time field value converted to a Date for the time picker. */
    public get endTimeAsDate(): Date {
        return this.managers.domain.convertTimeToDate(this.getValue("endTime"));
    }

    /** Clears validation errors on key form fields. */
    public removeError(): void {
        this.setError("selectedStudentId", "");
        this.setError("pickUpAddressId", "");
        this.setError("dropOffAddressId", "");
        this.setError("startTime", "");
        this.setError("endTime", "");
    }

    public get canSubmitForm(): boolean {
        return this.isModelValid();
    }

    // ─── Field update commands — delegated to CommandsManager ────────────────

    public get updateStudentOptionCommand() {
        return this.managers.commands.updateStudentOptionCommand;
    }
    public get updateStudentPickupAddressCommand() {
        return this.managers.commands.updatePickupAddressCommand;
    }
    public get updateStudentDropAddressCommand() {
        return this.managers.commands.updateDropOffAddressCommand;
    }
    public get updateStartTimeCommand() {
        return this.managers.commands.updateStartTimeCommand;
    }
    public get updateEndTimeCommand() {
        return this.managers.commands.updateEndTimeCommand;
    }

    // ─── Contextual commands (require parent ViewModel reference) ────────────
    // These are factory methods, not getter properties, because they need context
    // from the parent ViewModel that the AddStudentViewModel doesn't own.

    /** Returns a command that selects a student and pre-fills session times. */
    public updateStudentWithPreFillCommand(parentViewModel: any) {
        return this.managers.commands.updateStudentWithPreFillCommand(parentViewModel);
    }

    /** Returns a command that saves the form and triggers parent rota refresh. */
    public saveMentorStudentCommand(parentViewModel: any, onSuccess: () => void) {
        return this.managers.commands.saveMentorStudentCommand(parentViewModel, onSuccess);
    }

    /** Returns a command that closes the modal and resets the form. */
    public closeCommand(onCancel: () => void) {
        return this.managers.commands.closeCommand(onCancel);
    }
}
